// Свитото/разгънатото странично меню на модулите със sidebar („Складова база",
// „Справки") се ПОМНИ между посещенията — в localStorage, по модул и по вид
// екран:
//   • десктоп (≥ 1024 px) — по подразбиране разгънато;
//   • таблет (640–1023 px) — по подразбиране рейл (автоматичното свиване).
// Изричният избор (бутонът „Свий/Разгъни") печели над подразбирането, но САМО
// за вида екран, на който е направен: свито на таблета не свива десктопа, а
// разгънато на десктопа не разгъва рейла на таблета, когато прозорецът се
// стесни. Под 640 px менюто е чекмедже — там състоянието не се пипа.
//
// Всяко четене/писане е в try/catch: без хранилище (частен прозорец, пълна
// квота, забранени данни) менюто работи както преди — само не помни. Записът
// чете наново и слива само своя вид екран, за да не затрие избора от друг таб.
import { useCallback, useState } from "react";
import { useViewport } from "./useViewport";

export type SidebarScreen = "desktop" | "tablet";
export type SidebarChoices = Partial<Record<SidebarScreen, boolean>>;

const SCREENS: SidebarScreen[] = ["desktop", "tablet"];

export const sidebarStorageKey = (module: string) => `sidebar_collapsed:${module}`;

export const readSidebarChoices = (module: string): SidebarChoices => {
  try {
    const raw = localStorage.getItem(sidebarStorageKey(module));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const choices: SidebarChoices = {};
    for (const screen of SCREENS) {
      const value = (parsed as Record<string, unknown>)[screen];
      if (typeof value === "boolean") choices[screen] = value;
    }
    return choices;
  } catch {
    return {};
  }
};

export const writeSidebarChoice = (module: string, screen: SidebarScreen, collapsed: boolean) => {
  try {
    const next = { ...readSidebarChoices(module), [screen]: collapsed };
    localStorage.setItem(sidebarStorageKey(module), JSON.stringify(next));
  } catch {
    // без хранилище менюто просто не помни — изборът важи до края на посещението
  }
};

// Изборът за този вид екран, иначе подразбирането (рейл на таблет)
export const collapsedFor = (choices: SidebarChoices, screen: SidebarScreen) =>
  choices[screen] ?? screen === "tablet";

// Видът екран по брейкпойнтите на `useViewport`; null — чекмедже (< 640 px)
export const sidebarScreenOf = (viewport: { isNarrow: boolean; isTablet: boolean }): SidebarScreen | null =>
  viewport.isNarrow ? null : viewport.isTablet ? "tablet" : "desktop";

export const useSidebarCollapse = (module: string) => {
  const viewport = useViewport();
  const screen = sidebarScreenOf(viewport);
  const [choices, setChoices] = useState<SidebarChoices>(() => readSidebarChoices(module));
  // В чекмеджето менюто е винаги разгънато (решава го самият sidebar)
  const collapsed = screen ? collapsedFor(choices, screen) : false;

  const toggle = useCallback(() => {
    if (!screen) return;
    const next = !collapsed;
    setChoices((current) => ({ ...current, [screen]: next }));
    writeSidebarChoice(module, screen, next);
  }, [collapsed, module, screen]);

  return { collapsed, toggle };
};
